import type { FetchableExtendedWarrantyInfo, StandardCart, StandardLineItem } from '@/types/ShopFront/CheckoutStandards'
import {
  array,
  assertion, bool, CheckerReturnType, literal, number, object, string,
} from '@recoiljs/refine'
import { error } from '@/services/Log'
import Axios from 'axios'
import { SHOP_ORIGIN } from '@/services/Configuration'
import { thenCaught } from '@/helpers/FP'
import { ClientSideCache } from '@/services/ClientSideCache'
import { EXTEND_SKU } from '@/data/constants/EXTEND_SKU'
import { SHIPPED_SHIELD_SKU } from '@/data/constants/SHIPPED_SHIELD_SKU'
import { formatPriceValue } from '@/helpers/common'
import { isFeatureEnabled } from '@/helpers/isFeatureEnabled'

const keepingRelevantSkus = <T extends { sku: string }>(items: readonly T[]): T[] => (
  items
    .filter((item) => (
      !item.sku.includes(EXTEND_SKU)
      && !item.sku.includes(SHIPPED_SHIELD_SKU)
    ))
)

const RequiredItemProps = object({
  id: string(),
  sku: string(),
  name: string(),
  salePrice: number(),
  quantity: number(),
  isReturnable: bool(),
  imageUrl: string(),
})

const requiredPropsForProtectionQuote = assertion(RequiredItemProps)
const responseShapeForQuotedItem = object({
  line_item_id: string(),
  product_id: string(),
  quantity: number(),
  price: number(),
})
type ResponseShapeForQuotedItem = CheckerReturnType<typeof responseShapeForQuotedItem>
const QuoteAPIResponse = object({
  quotes: array(object({
    quote_id: string(),
    price: number(),
    status: literal('accepted'),
    line_items: array(responseShapeForQuotedItem),
  })),
})

const assertQuoteAPIResponse = assertion(QuoteAPIResponse)
export type QuoteAPIResponseType = CheckerReturnType<typeof QuoteAPIResponse>

const sadPath = (cart: StandardCart) => (err: unknown): FetchableExtendedWarrantyInfo => {
  error(`Failed to get worry-free quote: ${String(err)}`)
  return {
    warrantyOfferId: null,
    warrantyOfferPrice: null,
    warrantyOfferItems: cart.warrantyOfferItems,
  }
}

type HashablePropsFromCartItem = {
  sku: string
  quantity: number
  salePrice: number
}

const getAdjustedSalePrice = (cartItem: StandardLineItem): number => {
  const salePrice = cartItem.salePrice - (cartItem.couponAmount / cartItem.quantity)
  return formatPriceValue(salePrice)
}

const currentQuotableItemsOnCart = (
  cart: StandardCart,
): Array<HashablePropsFromCartItem> => (
  cart.lineItems
    .map((item) => ({
      sku: item.sku,
      quantity: item.quantity,
      salePrice: getAdjustedSalePrice(item),
    }))
    .filter((item) => (
      !item.sku.includes(EXTEND_SKU)
      && !item.sku.includes(SHIPPED_SHIELD_SKU)
    ))
)

const hashableItemToString = (item: HashablePropsFromCartItem): string => (
  `${item.sku}-${item.quantity}-${item.salePrice}`
)

const shouldFetchNewQuote = (cart: StandardCart): boolean => {
  const expectedItems = currentQuotableItemsOnCart(cart)
  return !(
    expectedItems.length === cart.warrantyOfferItems.length
    && expectedItems.every((item) => (
      cart.warrantyOfferItems.some((warrantyItem) => (
        warrantyItem.sku === item.sku
        && warrantyItem.quantity === item.quantity
        && warrantyItem.skuPrice === item.salePrice
      ))
    ))
  )
}

const adjustQuotableItems = (cart: StandardCart) => (
  cart.lineItems.map((lineItem) => ({
    ...lineItem,
    salePrice: getAdjustedSalePrice(lineItem),
  }))
)

const fetchNewQuote = async (cart: StandardCart): Promise<QuoteAPIResponseType> => {
  const { data } = await Axios.post<unknown>(`${SHOP_ORIGIN}/oauth/worry-free-delivery/quote`, {
    cartId: cart.id,
    items: (
      keepingRelevantSkus(adjustQuotableItems(cart))
        .map(requiredPropsForProtectionQuote)
    ),
  })
  return assertQuoteAPIResponse(data)
}
// Parse quote reponse core attributes
const getQuoteItemCoreattributes = (quoteItem: ResponseShapeForQuotedItem) => ({
  sku: quoteItem.product_id,
  quantity: quoteItem.quantity,
  skuPrice: quoteItem.price,
})
const withWorryFreeQuoteHappyPath = async (
  cart: StandardCart,
): Promise<FetchableExtendedWarrantyInfo> => {
  if (!isFeatureEnabled('worryFreeDelivery')) {
    throw new Error('WFD disabled')
  }
  if (!shouldFetchNewQuote(cart)) {
    return {
      warrantyOfferId: cart.warrantyOfferId,
      warrantyOfferPrice: cart.warrantyOfferPrice,
      warrantyOfferItems: cart.warrantyOfferItems,
    } as FetchableExtendedWarrantyInfo
  }
  const { quotes } = await fetchNewQuote(cart)
  return {
    warrantyOfferId: quotes.map((quote) => ({
      quoteId: quote.quote_id,
      ...getQuoteItemCoreattributes(quote.line_items[0]),
      quotePrice: quote.price,
    })),
    warrantyOfferPrice: quotes.reduce((acc, quote) => acc + quote.price, 0),
    warrantyOfferItems: quotes.map((quote) => getQuoteItemCoreattributes(quote.line_items[0])),
  }
}

const sortbasedOnSku = (a: { sku: string }, b: { sku: string }): number => (
  a.sku.localeCompare(b.sku)
)

const hashCartItems = (cart: StandardCart): string => (
  keepingRelevantSkus(currentQuotableItemsOnCart(cart))
    .sort(sortbasedOnSku)
    .map(hashableItemToString)
    .join(',')
)

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const cacheByQuotableItems = ClientSideCache('services/Cart/withWorryFreeQuote.ts:cacheByQuotableItems')(hashCartItems)

export const withWorryFreeQuote = async (
  cart: StandardCart,
): Promise<FetchableExtendedWarrantyInfo> => (
  thenCaught(withWorryFreeQuoteHappyPath)(sadPath(cart))(cart)
)
